import { IntlShape } from 'react-intl';
import { StepOptions } from 'shepherd.js';
import { NavigateFunction } from 'react-router-dom';

const wait = (duration: number) => new Promise((resolve) => setTimeout(resolve, duration));

const tours: Record<string, Partial<StepOptions>[]> = {
  genericTour: [
    {
      id: 'euc.generic',
    },
    {
      id: 'platforms.generic',
      attachTo: {
        element: '.platforms-link',
        on: 'bottom',
      },
      beforeShowPromise: () => wait(500),
    },
    {
      id: 'explore.generic',
      attachTo: {
        element: '.explore-link',
        on: 'bottom',
      },
      beforeShowPromise: () => wait(500),
    },
    {
      id: 'declare.generic',
      attachTo: {
        element: '.declare-link',
        on: 'bottom',
      },
      beforeShowPromise: () => wait(500),
    },
    {
      id: 'certification.generic',
      attachTo: {
        element: '.certification-link',
        on: 'bottom',
      },
      beforeShowPromise: () => wait(500),
    },
    {
      id: 'monitoring.generic',
      attachTo: {
        element: '.monitoring-link',
        on: 'bottom',
      },
      beforeShowPromise: () => wait(500),
    },
    {
      id: 'documentation.generic',
      attachTo: {
        element: '.documentation-link',
        on: 'bottom',
      },
      beforeShowPromise: () => wait(500),
    },
    {
      id: 'download.generic',
      attachTo: {
        element: '.download-link',
        on: 'bottom',
      },
      beforeShowPromise: () => wait(500),
    },
  ],
  declareTour: [
    {
      id: 'option.declare',
    },
    {
      id: 'declare.one',
    },
    {
      id: 'declare.two',
      attachTo: {
        element: '.general-section',
        on: 'right',
      },
    },
    {
      id: 'declare.three',
      attachTo: {
        element: '.technical-section',
        on: 'right',
      },
    },
    {
      id: 'declare.four',
      attachTo: {
        element: '.usage-section',
        on: 'right',
      },
    },
    {
      id: 'declare.five',
      attachTo: {
        element: '.security-section',
        on: 'right',
      },
    },
    {
      id: 'declare.six',
      attachTo: {
        element: '.information-section',
        on: 'left',
      },
    },
    {
      id: 'declare.seven',
      attachTo: {
        element: '.organization-section',
        on: 'left',
      },
    },
    {
      id: 'declare.eight',
      attachTo: {
        element: '.people-section',
        on: 'left',
      },
    },
    {
      id: 'declare.nine',
      attachTo: {
        element: '.actions-buttons',
        on: 'right',
      },
    },
    {
      id: 'declare.ten',
      attachTo: {
        element: '.hint-icon',
        on: 'right',
      },
    },
  ],
  certificationTour: [
    {
      id: 'option.certification',
    },
    {
      id: 'certification.one',
    },
  ],
};

const stepNextPage: Record<string, string> = {
  'euc.generic': '/explore/platforms',
  'platforms.generic': '/explore/all',
  'explore.generic': '/add/project',
  'declare.generic': '/certification',
  'certification.generic': '/monitoring',
  'monitoring.generic': '/documentation',
  'documentation.generic': '/download',
};

export const getTourSteps = (intl: IntlShape, navigate: NavigateFunction, location: string): Array<StepOptions> => {
  const steps: Partial<StepOptions>[] = [];

  const getButtons = (intl: IntlShape, index: number, stepsCount: number, stepId: string | undefined) => {
    const buttons = [];
    if (stepId === 'option.certification' || stepId === 'option.declare') {
      buttons.push(
        {
          text: intl.formatMessage({ id: 'tour.button.close' }),
          type: 'hide',
        },
        {
          text: intl.formatMessage({ id: `tour.button.${stepId}` }),
          type: 'next',
        },
        {
          text: intl.formatMessage({ id: 'tour.button.fullTour' }),
          type: '',
          action() {
            this.addSteps(getFullSteps(intl, tours['genericTour']));
            return this.show('euc.generic', true);
          },
        }
      );
    } else {
      if (index > 0) {
        buttons.push({
          text: intl.formatMessage({ id: 'tour.button.previous' }),
          type: 'back',
          secondary: true,
          action() {
            return this.back(); //explicitly move back -- needed after adding tour steps
          },
        });
      }
      if (index === stepsCount - 1) {
        buttons.push({
          text: intl.formatMessage({ id: 'tour.button.close' }),
          type: 'hide',
          action() {
            return this.complete(); //explicitly close tour -- needed after adding tour steps
          },
        });
      } else {
        buttons.push({
          text: intl.formatMessage({ id: 'tour.button.next' }),
          // type: 'next',
          action() {
            if (stepId && /[a-z]*\.generic$/.test(stepId) && stepNextPage[stepId]) {
              navigate(stepNextPage[stepId]);
            }
            return this.next(); //explicitly move to the next step - needed after adding tour steps, all doesn't work otherwise for some reason
          },
        });
      }
    }
    return buttons;
  };

  const getFullSteps = (intl: IntlShape, partialSteps: Partial<StepOptions>[]): Array<StepOptions> => {
    const fullSteps: Array<StepOptions> = partialSteps.map((step: Partial<StepOptions>, index: number) => ({
      title: intl.formatMessage({ id: `tour.step.${step.id}.title` }),
      text: intl.formatMessage({ id: `tour.step.${step.id}.text` }),
      buttons: step.advanceOn ? [] : getButtons(intl, index, partialSteps.length, step.id),
      highlightClass: step.id && new RegExp('[a-z]*.generic$').test(step.id) ? '' : 'tour-highlighted-item',
      scrollTo: {
        behavior: 'smooth',
        block: 'center',
      },
      cancelIcon: {
        enabled: true,
      },
      when: {
        show() {
          const currentStepElement = (this as any).el;
          const footer = currentStepElement.querySelector('.shepherd-footer');
          const header = currentStepElement.querySelector('.shepherd-header');
          const progress = document.createElement('span');
          progress.classList.add('shepherd-progress');
          progress.innerHTML = intl.formatMessage(
            { id: 'tour.progression' },
            { step: index + 1, total: partialSteps.length }
          );
          if (footer) {
            footer.insertBefore(progress, currentStepElement.querySelector('.shepherd-button'));
          } else if (header && step.advanceOn) {
            progress.style.marginRight = '20px';
            header.insertBefore(progress, currentStepElement.querySelector('.shepherd-cancel-icon'));
          }
        },
        hide: () => {},
      },
      ...step,
    }));
    return fullSteps;
  };

  if (location === '/add/project') {
    steps.push(...tours['declareTour']);
  } else if (location.includes('/certification')) {
    steps.push(...tours['certificationTour']);
  } else {
    steps.push(...tours['genericTour']);
  }

  return getFullSteps(intl, steps);
};
